import { useGlobalState } from '@/hooks';
import { updateShipmentTimeout } from '@/modules/pmt/fetchers';
import { type IShipmentData, ShipmentSearchStatus } from '@/modules/pmt/interfaces';
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';

const TIMEOUT_SECONDS = 30;
const calculateTimeoutDelay = (time: string): number => {
  const now = dayjs();
  const elapsedSeconds = now.diff(dayjs(time), 'second');
  if (elapsedSeconds >= TIMEOUT_SECONDS) {
    return 0;
  }

  return (TIMEOUT_SECONDS - elapsedSeconds) * 1000;
};

const getTimeout = (delayMs: number, onTimeout: () => void): NodeJS.Timeout | undefined => {
  if (delayMs <= 0) {
    onTimeout();
    return;
  }

  return setTimeout(onTimeout, delayMs);
};

export const useUpdateTimeoutShipment = (shipment?: IShipmentData, callback?: () => void) => {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const { firestoreUuid } = useGlobalState();

  // Store callback in ref to avoid adding it to dependency array
  const cbRef = useRef(callback);
  cbRef.current = callback;

  // Handle timeout when searching shipment
  useEffect(() => {
    const delayMs = shipment?.createdAt ? calculateTimeoutDelay(shipment.createdAt) : TIMEOUT_SECONDS * 1000;
    timeoutRef.current = getTimeout(delayMs, () => {
      if (
        !firestoreUuid ||
        !shipment?.createdAt ||
        !shipment?.parcel?.parcelId ||
        shipment?.searchStatus !== ShipmentSearchStatus.Pending
      ) {
        return;
      }
      cbRef.current?.();

      const firestoreDocId = shipment.firestoreDocId;
      const trackingNumber = shipment.parcel.parcelId;
      updateShipmentTimeout({ firestoreUuid, firestoreDocId, trackingNumber });
    });

    return () => {
      timeoutRef.current && clearTimeout(timeoutRef.current);
    };
  }, [
    firestoreUuid,
    shipment?.searchStatus,
    shipment?.parcel?.parcelId,
    shipment?.firestoreDocId,
    shipment?.createdAt,
  ]);

  // Handle timeout when scraping shipment with carrier
  useEffect(() => {
    if (
      !firestoreUuid ||
      !shipment?.updatedAt ||
      !shipment?.parcel?.parcelId ||
      shipment?.searchStatus !== ShipmentSearchStatus.ScrapingWithCarrier
    ) {
      return;
    }

    const delayMs = calculateTimeoutDelay(shipment.updatedAt);

    timeoutRef.current = getTimeout(delayMs, () => {
      updateShipmentTimeout({
        firestoreUuid,
        firestoreDocId: shipment.firestoreDocId,
        trackingNumber: shipment.parcel?.parcelId,
      });
    });

    return () => {
      timeoutRef.current && clearTimeout(timeoutRef.current);
    };
  }, [
    firestoreUuid,
    shipment?.updatedAt,
    shipment?.searchStatus,
    shipment?.parcel?.parcelId,
    shipment?.firestoreDocId,
  ]);

  // Clear timeout when shipment is fetched or not found
  useEffect(() => {
    if (
      shipment?.searchStatus === ShipmentSearchStatus.Timeout ||
      shipment?.searchStatus === ShipmentSearchStatus.Fetched
    ) {
      timeoutRef.current && clearTimeout(timeoutRef.current);
    }
  }, [shipment?.searchStatus]);
};
